<template>
  <div class="campo-data-intervalo-container mb-3" :class="containerClass">
    <label
      v-if="label && !hideLabel"
      :for="id"
      class="form-label"
    >
      {{ label }}
    </label>

    <VueDatePicker
      ref="pickerRef"
      v-model="date"
      :range="true"
      :preset-dates="presetDates"
      :dark="isDarkMode"
      format="dd/MM/yyyy"
      locale="pt-BR"
      cancelText="Cancelar"
      selectText="Selecionar"
      placeholder="Selecione o período"
      :enable-time-picker="false"
      auto-apply
      :disabled="disabled"
      input-class-name="form-control"
      position="left"
      @open="onPickerOpen"
      @closed="onPickerClosed"
    >
      <template #preset-date-range-button="{ label, value, presetDate }">
        <span
          role="button"
          :tabindex="0"
          @click="presetDate(value)"
          @keyup.enter.prevent="presetDate(value)"
          @keyup.space.prevent="presetDate(value)"
        >
          {{ label }}
        </span>
      </template>
    </VueDatePicker>

    <div v-if="dica" class="form-text text-body-secondary small">
      {{ dica }}
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, default: '' },
  modelValueInicio: { type: String, default: '' }, // Data (YYYY-MM-DD)
  modelValueFim: { type: String, default: '' }, // Data (YYYY-MM-DD)
  disabled: { type: Boolean, default: false },
  hideLabel: { type: Boolean, default: false },
  containerClass: { type: String, default: '' },
  dica: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValueInicio', 'update:modelValueFim'])

const pickerRef = ref(null)
const date = ref(null)
const isDarkMode = ref(false)
let themeObserver = null

// Tracking para capturar clique em data única
const pendingClickedDate = ref(null)
let clickHandlerCleanup = null

onMounted(() => {
  const htmlEl = document.documentElement
  isDarkMode.value = htmlEl.getAttribute('data-bs-theme') === 'dark'

  themeObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'data-bs-theme') {
        isDarkMode.value = htmlEl.getAttribute('data-bs-theme') === 'dark'
      }
    })
  })

  themeObserver.observe(htmlEl, { attributes: true })
})

onUnmounted(() => {
  if (themeObserver) themeObserver.disconnect()
  removeClickHandler()
})

const getStartOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
const getEndOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0)
const getStartOfYear = (d) => new Date(d.getFullYear(), 0, 1)
const getEndOfYear = (d) => new Date(d.getFullYear(), 11, 31)
const getSubMonths = (d, months) => {
  const dt = new Date(d)
  dt.setMonth(dt.getMonth() - months)
  return dt
}
const getSubDays = (d, days) => {
  const dt = new Date(d)
  dt.setDate(dt.getDate() - days)
  return dt
}

const hoje = new Date()

const presetDates = ref([
  { label: 'Hoje', value: [hoje, hoje] },
  { label: 'Últimos 7 dias', value: [getSubDays(hoje, 7), hoje] },
  { label: 'Últimos 30 dias', value: [getSubDays(hoje, 30), hoje] },
  { label: 'Este Mês', value: [getStartOfMonth(hoje), getEndOfMonth(hoje)] },
  { label: 'Mês Passado', value: [getStartOfMonth(getSubMonths(hoje, 1)), getEndOfMonth(getSubMonths(hoje, 1))] },
  { label: 'Este Ano', value: [getStartOfYear(hoje), getEndOfYear(hoje)] }
])

const parseDateString = (dateStr) => {
  if (!dateStr) return null
  const parts = dateStr.split('T')[0].split('-')
  if (parts.length !== 3) return null
  return new Date(parts[0], parts[1] - 1, parts[2])
}

const formatDateString = (dateObj) => {
  if (!dateObj) return ''
  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

watch(() => [props.modelValueInicio, props.modelValueFim], ([inicio, fim]) => {
  if (inicio && fim) {
    date.value = [parseDateString(inicio), parseDateString(fim)]
  } else if (!inicio && !fim) {
    date.value = null
  }
}, { immediate: true })

watch(date, (newVal) => {
  if (newVal && Array.isArray(newVal) && newVal.length === 2 && newVal[0] && newVal[1]) {
    emit('update:modelValueInicio', formatDateString(newVal[0]))
    emit('update:modelValueFim', formatDateString(newVal[1]))
  } else {
    emit('update:modelValueInicio', '')
    emit('update:modelValueFim', '')
  }
})

// --- Manipulação de clique único no calendário ---

/**
 * Extrai um Date a partir do id da célula do calendário.
 * O VueDatePicker v7 coloca o id no formato "YYYY-MM-DD" em cada .dp__calendar_item.
 */
const parseDateFromCellId = (id) => {
  if (!id) return null
  const parts = id.split('-')
  if (parts.length !== 3) return null
  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10) - 1
  const day = parseInt(parts[2], 10)
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null
  return new Date(year, month, day)
}

const removeClickHandler = () => {
  if (clickHandlerCleanup) {
    clickHandlerCleanup()
    clickHandlerCleanup = null
  }
}

const onCalendarDayClick = (e) => {
  const cell = e.target.closest('.dp__calendar_item')
  if (!cell) return
  const cellId = cell.getAttribute('id')
  if (!cellId) return

  const parsed = parseDateFromCellId(cellId)
  if (parsed) {
    pendingClickedDate.value = parsed
  }
}

const onPickerOpen = () => {
  pendingClickedDate.value = null
  removeClickHandler()

  nextTick(() => {
    const el = pickerRef.value?.$el
    if (!el) return

    el.addEventListener('click', onCalendarDayClick, true)
    clickHandlerCleanup = () => {
      el.removeEventListener('click', onCalendarDayClick, true)
    }
  })
}

const onPickerClosed = () => {
  removeClickHandler()

  // Se um range completo foi formado (2 cliques), o date.value já foi atualizado
  if (date.value && Array.isArray(date.value) && date.value[0] && date.value[1]) {
    return
  }

  // Se não houve range completo mas capturamos um clique em data única
  if (pendingClickedDate.value) {
    const d = pendingClickedDate.value
    date.value = [new Date(d.getTime()), new Date(d.getTime())]
    pendingClickedDate.value = null
  }
}
</script>

<style scoped>
.form-label { font-weight: 400 !important; }

/* Customização para integrar com o Bootstrap 5 e dar mais destaque (box-shadow) */
:deep(.dp__input) {
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  padding-left: 35px !important; /* Espaço para o ícone de calendário */
  border-radius: 0.375rem;
  border-color: var(--bs-border-color);
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  font-family: inherit;
  font-size: 1rem;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05); /* Sombra suave para separar do fundo */
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

:deep(.dp__input:hover) {
  border-color: var(--bs-primary);
}

:deep(.dp__input_focus) {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
}

/* Ajustes Específicos para o Modo Escuro */
:global([data-bs-theme="dark"]) :deep(.dp__input) {
  background-color: var(--bs-tertiary-bg); /* Fundo um pouco mais claro que o body */
  border-color: var(--bs-border-color-translucent);
  box-shadow: 0 2px 5px rgba(0,0,0,0.25);
}

:global([data-bs-theme="dark"]) :deep(.dp__input_focus) {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.5) !important;
}
</style>
